import React from "react";
import { Bell, Search, Download, RefreshCw, ChevronDown, Filter, User } from "lucide-react";
import { useHSE } from "../../lib/store";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from "../ui/dropdown-menu";
import { exportCSV } from "../../lib/exportUtils";
import { toast } from "sonner";

const MONTHS = ["All", "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function Selector({ label, value, options, onChange, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testid}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-xs transition shadow-sm"
        >
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
          <span className="font-semibold max-w-[150px] truncate">{value}</span>
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((o, i) => (
          <DropdownMenuItem key={i} onClick={() => onChange(o.value ?? o)} data-testid={`${testid}-option-${i}`}>
            {o.label ?? o}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { filters, setFilters, data, lastRefreshed, refresh, siteNames, syncStatus } = useHSE();
  const { session } = useAuth();

  const monthOptions = MONTHS.map((m, i) => ({ label: m, value: i === 0 ? "All" : String(i - 1) }));
  const currentYear = new Date().getFullYear();
  const yearOptions = [String(currentYear - 1), String(currentYear), String(currentYear + 1)];
  const siteOptions = ["All Sites", ...siteNames];
  const checklistOptions = ["All Checklist Types", ...(data.settings.checklistTypes || [])];
  const equipmentOptions = ["All Equipment Categories", ...(data.settings.equipmentCategories || [])];
  const statusOptions = ["All Statuses", ...(data.settings.statusValues || [])];
  const riskOptions = ["All Risk Levels", ...(data.settings.riskLevels || [])];
  const monthLabel = filters.month === "All" ? "All" : MONTHS[Number(filters.month) + 1];

  const handleExport = async () => {
    if (window.location.pathname === "/" && document.getElementById("executive-dashboard")) {
      try {
        const { exportElementPDF } = await import("../../lib/exportUtils");
        toast.info("Generating dashboard PDF - please wait...");
        await exportElementPDF("executive-dashboard", `MWY-Construction-HSE-Dashboard-${filters.year}.pdf`);
        toast.success("Dashboard PDF exported");
      } catch {
        toast.error("Could not generate PDF");
      }
      return;
    }
    const rows = [
      ...(data.incidents || []).map((r) => ({ module: "Incidents", ...r })),
      ...(data.inspections || []).map((r) => ({ module: "Inspections", ...r })),
      ...(data.actions || []).map((r) => ({ module: "Action Plan", ...r })),
      ...(data.ptws || []).map((r) => ({ module: "Permit to Work", ...r })),
      ...(data.trainings || []).map((r) => ({ module: "Training", ...r })),
      ...(data.observations || []).map((r) => ({ module: "Observations / SOR", ...r })),
      ...(data.ncrs || []).map((r) => ({ module: "NCR Register", ...r })),
      ...(data.audits || []).map((r) => ({ module: "Audits", ...r })),
      ...(data.waste || []).map((r) => ({ module: "Waste / Environment", ...r })),
      ...(data.equipmentTools || []).map((r) => ({ module: "Equipment & Power Tools", ...r })),
      ...(data.checklistTemplates || []).map((r) => ({ module: "Checklist Templates", ...r })),
      ...(data.dailyChecklists || []).map((r) => ({ module: "Daily Safety Checklist", ...r })),
    ];
    exportCSV(rows, "mwy-construction-hse-overview.csv");
    toast.success("Overview report exported");
  };

  const lastSyncLabel = (() => {
    const t = new Date(lastRefreshed);
    const diff = Math.floor((Date.now() - t.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    return t.toLocaleString();
  })();

  return (
    <header data-testid="app-header" className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <div className="px-5 lg:px-7 pt-3.5 pb-3 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <div className="hidden md:block w-1 h-6 rounded-full" style={{ background: "#18B84F" }} />
            <div>
              <h1 className="font-heading text-[18px] md:text-[20px] font-bold leading-tight tracking-tight text-[#082B4D]">
                MWY Construction HSE & Internal Audit System
              </h1>
              <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                <span className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${syncStatus === "saving" ? "bg-amber-500 animate-pulse" : syncStatus === "offline" ? "bg-rose-500" : syncStatus === "loading" ? "bg-slate-400 animate-pulse" : "bg-[#18B84F] animate-pulse"}`} />
                  {syncStatus === "saving" ? "Saving..." : syncStatus === "offline" ? "Offline" : syncStatus === "loading" ? "Loading..." : "Cloud System ACTIVE"}
                </span>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="font-mono">Client: <span className="text-[#082B4D] font-semibold">{data.settings.client}</span></span>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="font-mono">Last synced: {lastSyncLabel}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 w-[280px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            data-testid="global-search-input"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search construction HSE records..."
            className="bg-transparent text-xs text-slate-700 placeholder-slate-400 focus:outline-none w-full"
          />
        </div>

        <button
          data-testid="header-refresh-btn"
          onClick={async () => { await refresh(); toast.success("Cloud system refreshed"); }}
          className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition text-slate-600"
          title="Refresh data"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <button
          data-testid="header-notifications-btn"
          className="relative p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition text-slate-600"
          title="Notifications"
        >
          <Bell className="w-4 h-4" />
        </button>

        <Button
          data-testid="header-export-btn"
          onClick={handleExport}
          size="sm"
          className="hidden md:inline-flex h-9 gap-1.5 text-xs font-semibold text-white shadow-sm"
          style={{ background: "#18B84F" }}
        >
          <Download className="w-3.5 h-3.5" /> Export Report
        </Button>

        <div className="flex items-center gap-2 pl-3 ml-1 border-l border-slate-200">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#082B4D] text-white shadow-sm">
            <User className="w-4 h-4" />
          </div>
          <div className="hidden lg:block">
            <div className="text-[12px] font-semibold text-[#082B4D] leading-tight">{session?.user?.name || "Muhammad Waqas Younas"}</div>
            <div className="text-[10px] text-slate-500">Construction HSE Admin</div>
          </div>
        </div>
      </div>

      <div className="px-5 lg:px-7 py-2 flex items-center gap-2 flex-wrap border-t border-slate-100 bg-slate-50/40">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5 mr-1">
          <Filter className="w-3 h-3" /> Filters
        </span>
        <Selector label="Site" value={filters.site} options={siteOptions}
          onChange={(v) => setFilters({ ...filters, site: v })} testid="filter-site" />
        <Selector label="Month" value={monthLabel} options={monthOptions}
          onChange={(v) => setFilters({ ...filters, month: v })} testid="filter-month" />
        <Selector label="Year" value={filters.year} options={yearOptions}
          onChange={(v) => setFilters({ ...filters, year: v })} testid="filter-year" />
        <Selector label="Checklist Type" value={filters.checklistType} options={checklistOptions}
          onChange={(v) => setFilters({ ...filters, checklistType: v })} testid="filter-checklist-type" />
        <Selector label="Equipment Category" value={filters.equipmentCategory} options={equipmentOptions}
          onChange={(v) => setFilters({ ...filters, equipmentCategory: v })} testid="filter-equipment-category" />
        <Selector label="Status" value={filters.status} options={statusOptions}
          onChange={(v) => setFilters({ ...filters, status: v })} testid="filter-status" />
        <Selector label="Risk / Priority" value={filters.riskLevel} options={riskOptions}
          onChange={(v) => setFilters({ ...filters, riskLevel: v })} testid="filter-risk-level" />

        <div className="ml-auto flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-400 font-mono">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#18B84F]" />
            {data.settings.sites.filter(s => typeof s === "object" ? s.status === "Active" : true).length} Active Sites
          </span>
          <span>-</span>
          <span>{data.equipmentTools?.length || 0} tools</span>
        </div>
      </div>
    </header>
  );
}

